import BigNumber from 'bignumber.js'

/**
 * Distributes `total` across installments proportionally to the weights the
 * API returned, instead of dumping the entire total/API delta onto the last
 * installment. Only the leftover cent rounding goes to the last installment,
 * so the sum always matches `total` exactly.
 */
export function distributeInstallmentsProportionally(
  apiValues: number[],
  total: number
): number[] {
  if (apiValues.length === 0) return []
  if (apiValues.length === 1) return [total]

  const responseTotal = apiValues.reduce((acc, cur) => acc + (cur || 0), 0)

  const values =
    responseTotal === 0
      ? apiValues.map(() =>
          BigNumber(total)
            .dividedBy(apiValues.length)
            .decimalPlaces(2, BigNumber.ROUND_HALF_UP)
            .toNumber()
        )
      : apiValues.map((value) =>
          BigNumber(total)
            .multipliedBy(value)
            .dividedBy(responseTotal)
            .decimalPlaces(2, BigNumber.ROUND_HALF_UP)
            .toNumber()
        )

  const sumWithoutLast = values
    .slice(0, -1)
    .reduce((acc, cur) => BigNumber(acc).plus(cur).toNumber(), 0)

  values[values.length - 1] = BigNumber(total).minus(sumWithoutLast).toNumber()

  return values
}
